import { useEffect } from "react";

import { CommandMenu } from "@/components/CommandMenu";
import { StatIndicator } from "@/components/Indicators";
import { PermissionGate } from "@/components/PermissionGate";
import { RoleCondition } from "@/components/RoleCondition";
import { SubHeader } from "@/components/SubHeader";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import type { TAccessionDetail } from "@/lib/types";
import {
  useInitializeNavStack,
  useNavLocationState,
} from "@/utils/useNavStack";
import { useSetRecentlyViewed } from "../store";
import { AccessionCommands } from "./AccessionCommands";
import { AccessionHeader } from "./AccessionHeader";
import { AccessionLayout } from "./AccessionLayout";
import { AccessionProvider } from "./AccessionProvider";
import { AccessionShortcuts } from "./AccessionShortcuts";
import {
  AccessionSideDetail,
  AccessionSideDetailButton,
} from "./AccessionSideDetail";
import { ActionButton } from "./ActionButton";
import { ActionDropdown } from "./ActionDropdown";
import { BottomTabs } from "./detail/BottomTabs";
import { HoldSection } from "./detail/HoldSection";
import { InstrumentResultsSection } from "./detail/InstrumentResultsSection";
import { ReportSection } from "./detail/ReportSection";
import { SamplesSection } from "./detail/SampleSection";
import { TestOrdersSection } from "./detail/TestOrderSection";

export function Accession({ accession }: { accession: TAccessionDetail }) {
  useInitializeNavStack();

  const recents = useSetRecentlyViewed();
  const { href, label } = useNavLocationState();

  useEffect(() => {
    recents.push(accession.id);
  }, [accession.id, recents.push]);

  return (
    <AccessionProvider accession={accession}>
      <AccessionLayout
        sideDetail={<AccessionSideDetail />}
        header={
          <SubHeader>
            <SubHeader.Left>
              <Breadcrumbs>
                <Breadcrumb href={href}>{label}</Breadcrumb>
                <Breadcrumb>
                  <span className="inline-flex items-center gap-1">
                    {accession.id}
                    {accession.priority === "STAT" && <StatIndicator />}
                  </span>
                </Breadcrumb>
              </Breadcrumbs>
            </SubHeader.Left>
            <SubHeader.Right>
              <div className="flex items-center gap-2">
                <RoleCondition
                  roles={[
                    "blis_lab_manager",
                    "blis_lab_tech",
                    "blis_lab_assistant",
                  ]}
                >
                  <ActionDropdown />
                </RoleCondition>
                <div className="lg:hidden">
                  <AccessionSideDetailButton />
                </div>
              </div>
            </SubHeader.Right>
          </SubHeader>
        }
      >
        <AccessionHeader
          actions={
            <RoleCondition
              roles={[
                "blis_lab_manager",
                "blis_lab_tech",
                "blis_lab_assistant",
              ]}
            >
              <ActionButton />
            </RoleCondition>
          }
        />
        <HoldSection />
        <PermissionGate requiredPermission="read:instrument_results">
          <InstrumentResultsSection />
        </PermissionGate>
        <ReportSection />
        <TestOrdersSection />
        <SamplesSection />
        <BottomTabs />
      </AccessionLayout>
      <AccessionShortcuts />
      <CommandMenu>
        <AccessionCommands />
      </CommandMenu>
    </AccessionProvider>
  );
}
